export interface MarvinWindow extends Window {
  marvin: Marvin | null;
}

// Based on https://marvinjs-demo.chemaxon.com/latest/jsdoc.html
export interface Marvin {
  ImageExporter: unknown;
  onLoad: unknown;
  onReady: (func: () => void) => void;
  onLoadArray: unknown;
  sketcherInstance: MarvinSketcher;
  Sketch: {
    license: (licenseURL: string, isDynamic?: boolean) => void;
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    addRequestInterceptor: (func: (config: any) => void) => void;
  };
}

// Based on https://marvinjs-demo.chemaxon.com/latest/jsdoc.html, see getDisplaySettings
export interface MarvinDisplaySettings {
  atomIndicesVisible: boolean;
  atomMapsVisible: boolean;
  backgroundColor: string; // css color string e.g. 'rgba(255,255,255,0)'
  carbonLabelVisible: boolean;
  chiralFlagVisible: boolean;
  circledsign: boolean;
  copyasmrv: boolean;
  cpkColoring: boolean;
  defaultTool: 'rectangleSelection' | 'lassoSelection' | 'rotate3d';
  disableContextMenu: boolean;
  displayMode: 'WIREFRAME' | 'BALLSTICK' | 'STICK' | 'SPACEFILL';
  eflowmidline: boolean;
  fogEffect3D: boolean;
  implicitHydrogen: 'ALL' | 'OFF' | 'HETERO' | 'TERMINAL_AND_HETERO';
  lonepaircalculationenabled: boolean;
  lonePairsVisible: boolean;
  mdlExportDisabled: boolean;
  toolbars:
    | 'standard'
    | 'education'
    | 'reaction'
    | 'markush'
    | 'search'
    | 'basicSearch'
    | 'reporting'
    | 'view3d'
    | 'ws_view3d';
  valenceErrorVisible: boolean;
}

export const enum MarvinChemicalFormat {
  // CDXML disabled until we have better support for it
  CDX = 'cdx',
  MRV = 'mrv',
  MOL = 'mol',
  MOLV3 = 'mol:V3',
  SMILES = 'smiles',
  INCHI = 'inchi',
  RDF = 'rdf',
}

interface Selection {
  atoms: string; // e.g. "1,2,3"
  bonds: string; // e.g. "1-2,2-3"
}

// Based on https://marvinjs-demo.chemaxon.com/latest/jsdoc.html, see setHighlight
interface Highlight {
  // give uids or indexes field (depend on how you would like to specify atoms/bonds to highlight)
  uids: {
    // string array with UIDs of atom
    atoms: Array<string>;
    // string array with UIDs of bond
    bonds: Array<string>;
  };
  indexes: {
    // string array of indeces of atom
    atoms: Array<string>;
    // string array of pair of atom indeces to describe bond
    bonds: Array<string>;
  };
  style: {
    // CSS color (rgb, rgba, hsl, hsla, hexa or web-safe color name)
    color: string;
    // value between 0.0 and 1.0 (default value is 1.0), if color has alpha value (rgb, rgba) this field is ignored.
    opacity: number;
    // size of the patch that is relative to the selection patch, 1.0 means the same patch than the selection.
    size: number;
  };
}

// Based on https://marvinjs-demo.chemaxon.com/latest/jsdoc.html, see `on`
export const enum MarvinEvent {
  MOLCHANGE = 'molchange',
  SELECTIONCHANGE = 'selectionchange',
  UNDO = 'undo',
  REDO = 'redo',
}

export interface MarvinButtonConfig {
  name: string;
  'image-url': string;
  toolbar: 'N' | 'S' | 'E' | 'W';
}

export interface MarvinTemplateConfig {
  structure: string;
  name: string;
  icon: string;
}

// Taken from https://marvinjs-demo.chemaxon.com/latest/jsdoc.html#
export interface MarvinSketcher {
  addButton(config: MarvinButtonConfig): void;
  addMolImportListener(callback: (...args: Array<unknown>) => void): void;
  addTemplate(config: MarvinTemplateConfig): void;
  clear(): void;
  exportStructure(format: MarvinChemicalFormat): Promise<string>;
  getSupportedFormats(): {
    importFormats: Array<MarvinChemicalFormat>;
    exportFormats: Array<MarvinChemicalFormat>;
  };
  getDisplaySettings(): MarvinDisplaySettings;
  getSelection(): Selection;
  importStructure(format: MarvinChemicalFormat | null, value: string): void;
  isEmpty(): boolean;
  on(event: MarvinEvent, callback: (...args: Array<unknown>) => void): void;
  off: ((callbackRef: string) => void) | ((event: MarvinEvent, callbackRef: string) => void);
  pasteStructure(format: MarvinChemicalFormat, value: string): Promise<unknown>;
  setAutoChirality(enable: boolean): void;
  setDisplaySettings(settings: Partial<MarvinDisplaySettings>): void;
  setHighlight(highlights: Array<Highlight>): void;
  setSelection(selection: Selection): void;
  setServices(serviceOptions: unknown): void;
}

// Do not use the CDN to serve the editor, as it requires setting specific headers in the CDN, and it's fine
// to use the main web server.
export const marvinJSSource = '/static/marvinjs/editorws.html';
export const marvinJSLicenseSource = '/1/api/marvin/license-grant';

export const MOLCONVERT_URL = '/1/api/marvin/molconvert';
export const CIP_STEREO_INFO_URL = '/1/api/marvin/cipStereoInfo';
export const CLEAN_URL = '/1/api/marvin/clean';
export const HYDROGENIZE_URL = '/1/api/marvin/hydrogenize';
